import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import LiveLocationCard from "../components/inhouse/LiveLocationCard";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Wrench, Warning, MapPin, Clock, ArrowRight, CalendarBlank, Folder, ListChecks, MapTrifold, ShieldCheck, FirstAid, Certificate, Camera, Money, CalendarCheck, Car, Invoice
} from "@phosphor-icons/react";
import OperativeScheduleCalendar from "../components/OperativeScheduleCalendar";
import NotificationPreferencesCard from "../components/NotificationPreferencesCard";
import CalendarSubscriptionCard from "../components/CalendarSubscriptionCard";
import AvailabilityCard from "../components/AvailabilityCard";
import PWAInstallCard from "../components/PWAInstallCard";
import { fmtDate } from "../lib/dateUtil";
import { Megaphone } from "@phosphor-icons/react";
import { MyTrainingCard } from "../components/hs/MyTrainingCard";
import { BulletinReadDialog } from "../components/hs/MyBulletinsList";
import { PortalTourOffer } from "../components/TourOffers";

/** Unsigned safety bulletins — read & sign straight from the home screen. Hidden when there are none. */
function MyBulletinsCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => API.get("/hse/bulletins/mine").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const todo = (data?.bulletins || []).filter((b) => !b.mine?.signed_at && b.status === "sent");
  if (!todo.length) return null;
  const critical = todo.some((b) => b.severity === "critical");
  return (
    <div className={`rounded-lg border p-5 ${critical ? "border-red-300 bg-red-50/60 dark:bg-red-950/20" : "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20"}`} data-testid="in-house-bulletins-card">
      <p className="text-xs uppercase tracking-wide font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1"><Megaphone className="h-4 w-4" weight="bold" /> Safety bulletin{todo.length === 1 ? "" : "s"} to sign</p>
      <p className="text-sm text-slate-700 dark:text-slate-200 mt-1" data-testid="in-house-bulletins-summary">{critical ? "Stop & read before your next job — " : ""}{todo.length} waiting for your signature</p>
      <div className="mt-3 space-y-1.5">
        {todo.slice(0, 3).map((b) => (
          <button key={b.id} type="button" onClick={() => setOpen(b)} className="w-full text-left flex items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm min-h-[44px] hover:border-primary/50" data-testid={`in-house-bulletin-row-${b.id}`}>
            <span className="font-medium truncate">{b.title}</span><span className={`text-xs shrink-0 font-medium ${b.severity === "critical" ? "text-red-600" : "text-amber-700"}`}>{b.severity_label} · Read & sign</span>
          </button>
        ))}
      </div>
      <BulletinReadDialog bulletin={open} onClose={() => setOpen(null)} onSigned={load} testid="in-house-bulletin" />
    </div>
  );
}

function fmtAddress(j) {
  return [j?.street_address, j?.town_city, j?.postcode].filter(Boolean).join(", ");
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    scheduled: "bg-blue-100 text-blue-800",
    in_progress: "bg-indigo-100 text-indigo-800",
    completed: "bg-green-100 text-green-800",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return (
    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {(status || "new").replace("_", " ")}
    </span>
  );
}

export default function InHousePortalPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [emergencyJobs, setEmergencyJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSupplierSide = ["vendor", "vendor_operative"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, j, e, p, t, po] = await Promise.all([
        API.get("/in-house-portal/dashboard"),
        API.get("/in-house-portal/jobs"),
        API.get("/in-house-portal/emergency-jobs"),
        API.get("/in-house-portal/projects"),
        API.get("/in-house-portal/tasks"),
        isSupplierSide ? Promise.resolve({ data: { pos: [] } }) : API.get("/in-house-portal/pos"),
      ]);
      setDashboard(d.data);
      setJobs(j.data.jobs || []);
      setEmergencyJobs(e.data.jobs || []);
      setProjects(p.data.projects || []);
      setTasks(t.data.tasks || []);
      setPos(po.data.pos || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load your work");
    } finally {
      setLoading(false);
    }
  }, [isSupplierSide]);

  useEffect(() => { load(); }, [load]);

  // Scroll to #schedule when the mobile bottom-nav Schedule tab is tapped.
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#schedule") {
      const el = document.getElementById("schedule");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      }
    }
  }, [location.hash, loading]);

  if (loading) {
    return <div className="p-8 text-center text-slate-400">Loading your work…</div>;
  }

  // Sort jobs chronologically — soonest scheduled first, undated jobs last.
  // Reads either `scheduled_start` or nested `schedule.start_date` so it
  // doesn't matter which storage format the job uses.
  const sortKey = (j) => {
    if (j.scheduled_start) return j.scheduled_start;
    const sd = j.schedule?.start_date;
    if (!sd) return "9999-12-31"; // undated → bottom
    const st = j.schedule?.start_time || "00:00";
    return `${sd}T${st.length === 5 ? st + ":00" : st}`;
  };
  const sortedJobs = [...jobs].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  return (
    <div className="p-6 space-y-6" data-testid="in-house-portal">
      {!isSupplierSide && <PortalTourOffer slug="op-welcome" blurb="Your jobs, the job page, schedule, route planner and help — narrated." />}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">My Work</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "there"}. {isSupplierSide ? "Jobs your company has assigned to you." : "Here's what's on your plate today."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "vendor" && (
            <Link to="/supplier-portal"><Button variant="outline" data-testid="back-to-supplier-portal-btn">Supplier portal</Button></Link>
          )}
          <a href="/emergency-card.html">
            <Button variant="outline" className="border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid="emergency-card-btn">
              <FirstAid className="h-4 w-4 mr-2" weight="bold" /> Emergency card
            </Button>
          </a>
          <Link to="/my-work/safety">
            <Button variant="outline" data-testid="my-safety-btn">
              <ShieldCheck className="h-4 w-4 mr-2" weight="bold" /> My Safety
            </Button>
          </Link>
          {!isSupplierSide && (
            <Link to="/my-work/certificates">
              <Button variant="outline" data-testid="my-certificates-btn">
                <Certificate className="h-4 w-4 mr-2" weight="bold" /> My Certificates
              </Button>
            </Link>
          )}
          {!isSupplierSide && (
            <Link to="/my-work/receipts">
              <Button variant="outline" data-testid="my-receipts-btn">
                <Camera className="h-4 w-4 mr-2" weight="bold" /> Snap a receipt
              </Button>
            </Link>
          )}
          {!isSupplierSide && (
            <Link to="/my-work/bills">
              <Button variant="outline" data-testid="my-bills-btn">
                <Invoice className="h-4 w-4 mr-2" weight="bold" /> Snap a bill
              </Button>
            </Link>
          )}
          {!isSupplierSide && (
            <Link to="/my-work/payslips">
              <Button variant="outline" data-testid="my-payslips-btn">
                <Money className="h-4 w-4 mr-2" weight="bold" /> My Payslips
              </Button>
            </Link>
          )}
          {!isSupplierSide && (
            <Link to="/my-work/leave">
              <Button variant="outline" data-testid="my-leave-btn">
                <CalendarCheck className="h-4 w-4 mr-2" weight="bold" /> My Leave
              </Button>
            </Link>
          )}
          {!isSupplierSide && (
            <Link to="/my-work/mileage">
              <Button variant="outline" data-testid="my-mileage-btn">
                <Car className="h-4 w-4 mr-2" weight="bold" /> Mileage
              </Button>
            </Link>
          )}
          <Link to="/my-work/route-planner">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="plan-my-day-btn">
              <MapTrifold className="h-4 w-4 mr-2" weight="bold" /> Plan My Day
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Wrench} label="Open jobs" value={dashboard?.jobs?.open || 0} tone="blue" />
        <StatCard icon={Folder} label="Active projects" value={dashboard?.projects?.active || 0} tone="indigo" />
        <StatCard icon={ListChecks} label="My tasks" value={dashboard?.tasks?.open || 0} tone="amber" />
        <StatCard icon={Warning} label="Open emergencies" value={dashboard?.emergency_jobs?.open || 0} tone="red" />
      </div>

      {/* PWA install prompt (mobile only, auto-hides when installed) */}
      <PWAInstallCard />

      <LiveLocationCard />

      {!isSupplierSide && <MyBulletinsCard />}
      {!isSupplierSide && <MyTrainingCard openTo="/my-work/safety" testid="in-house-training-card" />}

      {/* Next job */}
      {dashboard?.next_job && (
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-5" data-testid="in-house-next-job">
          <p className="text-xs uppercase tracking-wide font-semibold text-blue-700 dark:text-blue-300">Up next</p>
          <div className="flex items-center justify-between mt-1 flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{dashboard.next_job.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-1">
                <MapPin className="h-4 w-4" /> {fmtAddress(dashboard.next_job) || "—"}
              </p>
            </div>
            <Link to={`/my-work/jobs/${dashboard.next_job.id}`}>
              <Button data-testid="in-house-next-job-open">Open <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      )}

      {/* Schedule calendar */}
      <div id="schedule" className="scroll-mt-20">
        <OperativeScheduleCalendar />
      </div>

      {/* My Purchase Orders (P283 — issued by admin so the operative can invoice us) */}
      {!!pos.length && (
        <Section title="My Purchase Orders" count={pos.length} accent="amber">
          <div className="space-y-2">
            {pos.slice(0, 5).map((p) => {
              const cls = p.status === "paid"
                ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/20"
                : p.status === "acknowledged"
                  ? "border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20"
                  : "border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20";
              return (
                <Link
                  key={p.id}
                  to={`/my-work/pos/${p.id}`}
                  className={`flex items-center justify-between gap-3 p-3 rounded-md border ${cls} hover:shadow-sm transition`}
                  data-testid={`my-po-${p.po_number}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{p.title || p.po_number}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {p.po_number} · {p.period_start || "—"} → {p.period_end || "—"} · £{Number(p.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <span className="text-xs font-medium capitalize text-muted-foreground shrink-0">{p.status}</span>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      {/* Emergency jobs */}
      {!!emergencyJobs.length && (
        <Section title="Emergency jobs" count={emergencyJobs.length} accent="red">
          {emergencyJobs.map((j) => <JobCard key={j.id} job={j} entityType="emergency_job" emergency />)}
        </Section>
      )}

      {/* Jobs */}
      <Section title="Assigned jobs" count={jobs.length}>
        {jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
            Nothing's assigned to you yet. Check back soon.
          </div>
        ) : sortedJobs.map((j) => <JobCard key={j.id} job={j} entityType="job" />)}
      </Section>

      {/* Projects */}
      <Section title="My projects" count={projects.length} accent="indigo">
        {projects.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg" data-testid="no-projects-empty">
            You're not on any projects yet.
          </div>
        ) : projects.map((p) => <ProjectCard key={p.id} project={p} />)}
      </Section>

      {/* Tasks */}
      <Section title="My tasks" count={tasks.length} accent="amber">
        {tasks.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg" data-testid="no-tasks-empty">
            No tasks assigned.
          </div>
        ) : tasks.map((t) => <TaskCard key={t.id} task={t} />)}
      </Section>

      <NotificationPreferencesCard />
      <CalendarSubscriptionCard />
      <AvailabilityCard />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const toneMap = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30",
    red: "text-red-600 bg-red-100 dark:bg-red-900/30",
    slate: "text-slate-600 bg-slate-100 dark:bg-slate-800",
    indigo: "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30",
    amber: "text-amber-600 bg-amber-100 dark:bg-amber-900/30",
  };
  return (
    <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-md flex items-center justify-center ${toneMap[tone] || toneMap.slate}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

function Section({ title, count, accent = "slate", children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        <Badge variant={accent === "red" ? "destructive" : "secondary"}>{count}</Badge>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ProjectCard({ project }) {
  const pct = project.my_task_count > 0
    ? Math.round((project.my_task_done / project.my_task_count) * 100)
    : 0;
  return (
    <Link to={`/in-house/projects/${project.id}`}
          className="block p-4 rounded-lg border border-border bg-card hover:border-primary/60 transition"
          data-testid={`in-house-project-${project.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Folder className="h-4 w-4 text-indigo-500 shrink-0" />
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{project.name}</h3>
            {project.is_team_member && <Badge variant="secondary" className="uppercase text-[9px]">Team</Badge>}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{project.reference}</p>
          {project.property_address && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {project.property_address}
            </p>
          )}
          {project.my_task_count > 0 && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span>{project.my_task_done} of {project.my_task_count} of my tasks done</span>
                <span className="font-semibold">{pct}%</span>
              </div>
              <Progress value={pct} className="h-1.5" />
            </div>
          )}
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400 shrink-0" />
      </div>
    </Link>
  );
}

function TaskCard({ task }) {
  const statusMap = {
    not_started: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    assigned: "bg-blue-100 text-blue-800",
    in_progress: "bg-amber-100 text-amber-800",
    completed_by_vendor: "bg-emerald-100 text-emerald-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
  };
  const cls = statusMap[task.status] || "bg-slate-100 text-slate-700";
  const overdue = task.due_date && task.status !== "approved" && new Date(task.due_date) < new Date();
  return (
    <Link to={`/in-house/projects/${task.project_id}/tasks/${task.id}`}
          className="block p-4 rounded-lg border border-border bg-card hover:border-primary/60 transition"
          data-testid={`in-house-task-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ListChecks className="h-4 w-4 text-amber-500 shrink-0" />
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{task.title}</h3>
            <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${cls}`}>{(task.status || "new").replace(/_/g, " ")}</span>
            {overdue && <Badge variant="destructive" className="uppercase text-[9px]">Overdue</Badge>}
          </div>
          {task.project_name && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">in {task.project_name}</p>
          )}
          {task.start_date && (
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1 inline-flex items-center gap-1 font-medium">
              <CalendarBlank className="h-3 w-3" /> Start {fmtDate(task.start_date)}{task.start_time ? ` · ${task.start_time}` : ""}
            </p>
          )}
          {task.due_date && (
            <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 inline-flex items-center gap-1">
              <CalendarBlank className="h-3 w-3" /> Due {fmtDate(task.due_date)}
            </p>
          )}
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400 shrink-0" />
      </div>
    </Link>
  );
}

function JobCard({ job, entityType, emergency }) {
  // Render the scheduled date next to the status badge. Reads either
  // top-level `scheduled_start` or nested `schedule.start_date`.
  // Times are stored as `YYYY-MM-DDTHH:MM:SS+00:00` (GMT wall-clock) — slice
  // directly rather than parsing via Date, which would shift into local TZ.
  const scheduledLabel = (() => {
    let iso = job.scheduled_start;
    if (!iso && job.schedule?.start_date) {
      const st = job.schedule.start_time || "00:00";
      iso = `${job.schedule.start_date}T${st.length === 5 ? st + ":00" : st}`;
    }
    if (!iso || typeof iso !== "string" || iso.length < 10) return null;
    const isoDate = iso.slice(0, 10);
    // Parse into local midnight so weekday/month formatting stays correct.
    const d = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    const datePart = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    const timePart = (job.schedule?.start_time || (iso.length >= 16 ? iso.slice(11, 16) : ""));
    return timePart ? `${datePart} · ${timePart}` : datePart;
  })();

  return (
    <Link to={`/my-work/${entityType === "emergency_job" ? "emergency" : "jobs"}/${job.id}`}
          className="block p-4 rounded-lg border border-border bg-card hover:border-primary/60 transition"
          data-testid={`in-house-${entityType}-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {emergency && <Badge variant="destructive" className="uppercase">Emergency</Badge>}
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{job.title}</h3>
            <StatusPill status={job.status} />
            {scheduledLabel && (
              <span
                className="text-[11px] font-medium text-slate-600 dark:text-slate-300 inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded"
                data-testid={`in-house-job-scheduled-${job.id}`}
              >
                <CalendarBlank className="h-3 w-3" />
                {scheduledLabel}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {fmtAddress(job) || job.property_name || "—"}
          </p>
          {job.arrived_at && (
            <p className="text-[11px] text-green-600 mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> On site since {new Date(job.arrived_at).toLocaleTimeString()}
            </p>
          )}
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400 shrink-0" />
      </div>
    </Link>
  );
}
